import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  alpha,
  Checkbox,
  IconButton,
  Toolbar,
  Tooltip,
  useTheme
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

function DisclosureList({
  filteredRows,
  selectedDisclosure,
  handleSelectDisclosure,
  onBulkDeleteRequest,
  clearBulkSelectionKey = 0
}) {
  const theme = useTheme();
  // 新增多选状态
  const [selectedItems, setSelectedItems] = useState(new Set());

  useEffect(() => {
    if (clearBulkSelectionKey > 0) {
      setSelectedItems(new Set());
    }
  }, [clearBulkSelectionKey]);

  // 切换单个项目选择状态
  const toggleItemSelection = (item) => {
    const newSelectedItems = new Set(selectedItems);

    if (newSelectedItems.has(item.d_id)) {
      newSelectedItems.delete(item.d_id);
    } else {
      newSelectedItems.add(item.d_id);
    }

    setSelectedItems(newSelectedItems);
  };

  // 全选/取消全选
  const toggleSelectAll = () => {
    if (selectedItems.size === filteredRows.length) {
      // 如果全部选中，则清空选择
      setSelectedItems(new Set());
    } else {
      // 否则选中所有
      setSelectedItems(new Set(filteredRows.map((row) => row.d_id)));
    }
  };

  const handleBulkDelete = () => {
    const ids = [...selectedItems];
    if (!ids.length || !onBulkDeleteRequest) return;
    onBulkDeleteRequest(ids);
  };

  // 判断是否选中
  const isItemSelected = (d_id) => selectedItems.has(d_id);

  return (
    <>
      {/* 工具栏显示选中的项目数量和操作按钮 */}
      {selectedItems.size > 0 && (
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            bgcolor: "background.paper",
            borderRadius: 2.5,
            mb: 1,
            border: "1px solid",
            borderColor: "divider",
            position: "sticky",
            top: 0,
            zIndex: 10,
            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`
          }}
        >
          <Typography
            sx={{ flex: "1 1 100%" }}
            color='inherit'
            variant='subtitle1'
            component='div'
          >
            Selected {selectedItems.size} items
          </Typography>
          <Tooltip title='Delete'>
            <IconButton onClick={handleBulkDelete} sx={{ color: "error.main" }}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      )}

      <Stack spacing={1}>
        {filteredRows.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 1,
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: alpha(theme.palette.grey[500], 0.04)
            }}
          >
            <Checkbox
              checked={
                filteredRows.length > 0 &&
                selectedItems.size === filteredRows.length
              }
              indeterminate={
                selectedItems.size > 0 &&
                selectedItems.size < filteredRows.length
              }
              onChange={toggleSelectAll}
              size='small'
              sx={{
                p: 0,
                mr: 1,
                "& .MuiSvgIcon-root": { fontSize: 18 }
              }}
            />
            <Typography variant='body2' color='text.secondary'>
              {selectedItems.size === filteredRows.length
                ? "Deselect All"
                : "Select All"}{" "}
              ({filteredRows.length})
            </Typography>
          </Box>
        )}

        {filteredRows.map((d) => {
          const isSelected = Boolean(
            selectedDisclosure?.d_id && selectedDisclosure.d_id === d.d_id
          );

          const isMultiSelected = isItemSelected(d.d_id);

          const tagList = d.disclosure_tags || [];
          const maxVisible = 6;
          const visibleTags = tagList.slice(0, maxVisible);
          const extra = tagList.length - visibleTags.length;

          return (
            <Box
              key={d.d_id}
              onClick={(e) => {
                if (!e.target.closest(".MuiCheckbox-root")) {
                  handleSelectDisclosure(d);
                }
              }}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor:
                  isSelected || isMultiSelected
                    ? alpha(theme.palette.primary.main, 0.04)
                    : "background.paper",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                boxShadow:
                  isSelected || isMultiSelected
                    ? `0 0 0 1px ${theme.palette.primary.main}`
                    : "none",

                "&:hover": {
                  bgcolor:
                    isSelected || isMultiSelected
                      ? alpha(theme.palette.primary.main, 0.06)
                      : alpha(theme.palette.grey[500], 0.04),
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  boxShadow:
                    isSelected || isMultiSelected
                      ? `0 0 0 1px ${theme.palette.primary.main}`
                      : `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
                  transform: "translateY(-1px)"
                }
              }}
            >
              {/* 标题行：加入复选框和状态指示器 */}
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Checkbox
                  className='MuiCheckbox-root'
                  checked={isMultiSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleItemSelection(d);
                  }}
                  size='small'
                  sx={{
                    p: 0,
                    mr: 0.5,
                    "& .MuiSvgIcon-root": { fontSize: 18 }
                  }}
                />

                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: d.is_active ? "success.main" : "text.disabled",
                    opacity: d.is_active ? 1 : 0.4,
                    flexShrink: 0
                  }}
                />

                <Typography
                  variant='subtitle2'
                  fontWeight={600}
                  sx={{
                    color: isSelected ? "primary.main" : "text.primary",
                    lineHeight: 1.5,
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    opacity: d.is_active ? 1 : 0.6
                  }}
                >
                  {d.title}
                </Typography>
              </Stack>

              <Stack
                direction='row'
                spacing={0.5}
                flexWrap='wrap'
                useFlexGap
                sx={{ gap: 0.5, pl: 1 }}
              >
                {visibleTags.map((t) => (
                  <Chip
                    key={t.t_id}
                    label={t.name}
                    size='small'
                    variant='filled'
                    sx={{
                      height: 24,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      bgcolor: alpha(theme.palette.grey[500], 0.1),
                      color: "text.secondary",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.grey[500], 0.2)
                      },
                      ...(isSelected && {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: "primary.main"
                      })
                    }}
                  />
                ))}

                {extra > 0 ? (
                  <Chip
                    label={`+${extra}`}
                    size='small'
                    variant='outlined'
                    sx={{
                      height: 24,
                      fontSize: "0.75rem",
                      color: "text.disabled",
                      borderColor: "divider"
                    }}
                  />
                ) : null}

                {tagList.length === 0 ? (
                  <Typography
                    variant='caption'
                    color='text.disabled'
                    sx={{ py: 0.25 }}
                  >
                    No tags
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        })}

        {filteredRows.length > 0 && (
          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={toggleSelectAll}
            >
              {selectedItems.size === filteredRows.length
                ? "Cancel All"
                : "Select All"}
            </Typography>
          </Box>
        )}
      </Stack>
    </>
  );
}

export default DisclosureList;
